import { computed, Directive, effect, ElementRef, HostBinding, HostListener, inject, input } from '@angular/core';
import { filter } from 'rxjs';
import { PopoverService } from 'shared/ui-common';

import { AnchoredComment, CommentType } from '../comment.model';
import { CommentService } from '../comment.service';
import { markCommentOnContainer, unmarkCommentOnContainer } from '../comment.service.util';
import { AddCommentPopoverComponent } from '../components/add-comment-popover/add-comment-popover.component';

@Directive({
  selector: '[ttCommentableElement]',
  standalone: true,
})
export class CommentableElementDirective {
  private commentsService = inject(CommentService, { optional: true });
  private element = inject(ElementRef<HTMLElement>);
  private popoverService = inject(PopoverService);

  public widgetId = input.required<string>();
  public elementId = input.required<string>();

  private commentElementId = computed(() => `tt-commentable-${this.widgetId()}-${this.elementId()}`);

  private comment = computed(() => {
    if (!this.commentsService) return null;

    return this.commentsService
      .comments()
      ?.find(
        (comment) =>
          comment.anchor.type === 'element' &&
          comment.anchor.widgetId === this.widgetId() &&
          comment.anchor.elementId === this.elementId() &&
          !this.commentsService?.isOriginalContentUnavailable(comment),
      );
  });

  private isCommentSelected = computed(() => {
    if (!this.commentsService || !this.comment() || !this.commentsService.selectedComment()?.comment) {
      return false;
    }

    return this.comment()!.id === this.commentsService.selectedComment().comment.id;
  });

  @HostBinding('class')
  public get hostClasses(): string {
    return this.commentElementId();
  }

  @HostListener('click', ['$event'])
  public onClick(event: Event) {
    if (!this.commentsService) return;

    if (!this.commentsService.initialized() || this.commentsService.readonly() || this.comment()) {
      return;
    }

    this.handleElementClick();
  }

  private wrapperClickHandler = (event: Event) => {
    if (!this.commentsService) return;
    if (!this.commentsService.initialized()) return;

    event.stopPropagation();
    this.handleElementClick();
  };

  constructor() {
    effect(() => {
      if (!this.commentsService) {
        unmarkCommentOnContainer(this.element.nativeElement);
        return;
      }

      const comment = this.comment();
      const selected = this.isCommentSelected();
      const native = this.element.nativeElement;

      if (comment) {
        markCommentOnContainer(comment, selected, native, (event) => this.wrapperClickHandler(event));
      } else {
        unmarkCommentOnContainer(native);
      }
    });
  }

  private handleElementClick() {
    if (!this.commentsService) {
      return;
    }

    if (this.comment()) {
      this.commentsService.selectCommentByCommentId(this.comment()!.id);
      return;
    }

    if (this.commentsService.readonly()) {
      return;
    }

    const allowedTypes = this.commentsService.getNewCommentTypes();
    if (allowedTypes.length === 0) {
      return;
    }

    const popoverRef = this.popoverService.open(AddCommentPopoverComponent, this.element, {
      data: { allowedTypes },
      positionStrategy: 'top-center',
      offsetY: 10,
      hasBackdrop: false,
    });

    popoverRef
      .afterClosed()
      .pipe(filter((type): type is CommentType => type !== undefined && type !== null))
      .subscribe((type) => this.addComment(type));
  }

  private addComment(type: CommentType) {
    if (!this.commentsService) {
      return;
    }

    if (this.commentsService.readonly()) {
      return;
    }

    const newComment: AnchoredComment = {
      id: crypto.randomUUID(),
      text: '',
      type,
      anchor: {
        type: 'element',
        widgetId: this.widgetId(),
        elementId: this.elementId(),
      },
    };

    markCommentOnContainer(newComment, true, this.element.nativeElement, (event) => this.wrapperClickHandler(event));

    this.commentsService.addComment(newComment);
  }
}
